/* hs-eslint ignored failing-rules */

/* eslint-disable react/prop-types */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import PortalIdParser from 'PortalIdParser';
import AvailableAdAccountRecord from 'ads-account-connection/lib/AvailableAdAccountRecord';
import AccountConnectionData from 'ads-account-connection/data/AccountConnectionData';
import { fetchOverview, fetchMyPermissions } from 'ads-account-connection/data/ProfileData';
import AdAccountConnectModal from 'ads-account-connection/components/AdAccountConnectModal';
import LoadingModal from 'ads-account-connection/components/ad-accounts/LoadingModal';
import { showAdAccountLimits } from 'ads-account-connection/lib/AuthSelectors';
import Networks from 'ads-account-connection/lib/Networks';
import UIModal from 'UIComponents/dialog/UIModal';
import AdwordsAdAccountSetupModal from 'ads-account-connection/components/ad-accounts/AdwordsAdAccountSetupModal';
import { ACCOUNT_SETUP_STEPS } from 'ads-account-connection/lib/AdwordsSetupStatus';
import { Metrics } from 'ads-account-connection/lib/Metrics';
import { List } from 'immutable';

const AdAccountConnectModalContainer = ({
  onFailure,
  network,
  auth,
  maxSelectableAccounts,
  onClickCreateAdAccount,
  allowDeselectInModals,
  showMissingPermissionsWarning,
  onAccountConnected,
  onComplete,
  trackEvent,
  networkUsersId,
  adwordsSelectedFromOnboarding,
  isDeveloperPortal
}) => {
  const [loadingModal, setLoadingModal] = useState(true);
  const [availableAdAccounts, setAvailableAdAccounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [showAdwordsAdCreationModal, setShowAdwordsAdCreationModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(ACCOUNT_SETUP_STEPS.CREATE_ACCOUNT);
  const [inviteLink, setInviteLink] = useState('');
  const [adAccountId, setAdAccountId] = useState('');
  useEffect(() => {
    if (fetched) {
      return;
    }

    setFetched(true);
    Promise.all([AccountConnectionData.fetchAvailableAdAccounts(network), fetchOverview(), fetchMyPermissions()]).then(([availableAccounts, {
      accounts
    }, permissions]) => {
      const newAvailableAdAccounts = new ImmutableList(availableAccounts).map(account => new AvailableAdAccountRecord(account, accounts, !!(permissions && permissions.find(perm => perm.adNetwork === account.adNetwork && perm.adAccountId === account.adAccountId))));
      setAvailableAdAccounts(newAvailableAdAccounts);
    }).catch(err => onFailure(err)).finally(() => setLoadingModal(false)).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }, [onFailure, network, fetched]);

  const onShowCreationModal = () => setShowAdwordsAdCreationModal(true);

  const onSetupComplete = useCallback(_adAccountId => {
    setLoading(true);

    let _availableAccounts;

    AccountConnectionData.fetchAvailableAdAccounts(network).then(availableAccounts => {
      _availableAccounts = availableAccounts;
      const connectedAccount = availableAccounts.find(availableAccount => availableAccount.adNetwork === Networks.ADWORDS && availableAccount.adAccountId === _adAccountId);
      const networkUserId = connectedAccount && connectedAccount.networkUserId;

      if (!networkUserId) {
        throw new Error('connected account not found');
      }

      return AccountConnectionData.selectAdAccounts(network, networkUserId, {
        [_adAccountId]: true
      });
    }).then(() => {
      if (onAccountConnected) {
        onAccountConnected({
          available: List(_availableAccounts),
          network,
          selected: ImmutableMap({
            adAccountId: true
          })
        });
        Metrics.counter('connect-account', {
          network
        }).increment();
      }

      onComplete();
    }).catch(err => onFailure(err)).finally(() => {
      setLoading(false);
      window.location.href = `/ads/${PortalIdParser.get()}/onboarding-wizard?network=ADWORDS`;
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }, [network, onAccountConnected, onComplete, onFailure]);
  const onSelectAccounts = useCallback(accounts => {
    setLoading(true);
    Promise.all(accounts.filter((__hasTracking, accountId) => {
      const availableAccount = availableAdAccounts.find(account => account.accountId === accountId);
      return !!availableAccount && !availableAccount.currentUserHasConnected;
    }).groupBy((__hasTracking, accountId) => {
      const availableAccount = availableAdAccounts.find(account => account.accountId === accountId);
      return !!availableAccount && availableAccount.networkUserId;
    }).map((accountsMap, networkUserId) => {
      return AccountConnectionData.selectAdAccounts(network, networkUserId, accountsMap.toJS());
    }).toArray()).then(() => {
      if (onAccountConnected) {
        onAccountConnected({
          available: availableAdAccounts,
          network,
          selected: accounts
        });
        Metrics.counter('connect-account', {
          network
        }).increment();
      }

      onComplete();
    }).catch(err => onFailure(err)).finally(() => setLoading(false)).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }, [network, availableAdAccounts, onFailure, onAccountConnected, onComplete]);
  const onCancelAccountSelection = useCallback(() => {
    if (onAccountConnected) {
      onAccountConnected({
        available: availableAdAccounts,
        network,
        selected: null
      });
    }

    onComplete();
  }, [network, onAccountConnected, availableAdAccounts, onComplete]);
  const noAdwordsAccounts = useMemo(() => availableAdAccounts && availableAdAccounts.isEmpty(), [availableAdAccounts]);

  if (network === Networks.ADWORDS && (noAdwordsAccounts || showAdwordsAdCreationModal || adwordsSelectedFromOnboarding)) {
    return /*#__PURE__*/_jsx(AdwordsAdAccountSetupModal, {
      onCancel: onCancelAccountSelection,
      onStepChange: setCurrentStep,
      currentStep: currentStep,
      inviteLink: inviteLink,
      setInviteLink: setInviteLink,
      adAccountId: adAccountId,
      setAdAccountId: setAdAccountId,
      networkUserId: networkUsersId,
      onSelect: onSetupComplete,
      refetchAccounts: () => setFetched(false),
      trackEvent: trackEvent,
      isDeveloperPortal: isDeveloperPortal
    });
  }

  return /*#__PURE__*/_jsx(UIModal, {
    width: 900,
    "data-test-id": "ad-account-connect-modal",
    children: loadingModal ? /*#__PURE__*/_jsx(LoadingModal, {
      network: network,
      maxSelectableAccounts: maxSelectableAccounts
    }) : /*#__PURE__*/_jsx(AdAccountConnectModal, {
      network: network,
      loading: loading,
      availableAccounts: availableAdAccounts,
      onSelect: onSelectAccounts,
      onCancel: onCancelAccountSelection,
      maxSelectableAccounts: maxSelectableAccounts,
      showAdAccountLimits: showAdAccountLimits(auth),
      onClickCreateAdAccount: onClickCreateAdAccount,
      allowDeselect: allowDeselectInModals,
      showMissingPermissionsWarning: showMissingPermissionsWarning,
      trackEvent: trackEvent,
      onShowCreationModal: onShowCreationModal,
      isDeveloperPortal: isDeveloperPortal
    })
  });
};

AdAccountConnectModalContainer.propTypes = {
  network: PropTypes.oneOf(Object.values(Networks)).isRequired,
  maxSelectableAccounts: PropTypes.number.isRequired,
  onAccountConnected: PropTypes.func,
  auth: PropTypes.object.isRequired,
  onFailure: PropTypes.func.isRequired,
  onClickCreateAdAccount: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  allowDeselectInModals: PropTypes.bool.isRequired,
  showMissingPermissionsWarning: PropTypes.bool.isRequired,
  trackEvent: PropTypes.func,
  adwordsSelectedFromOnboarding: PropTypes.bool,
  isDeveloperPortal: PropTypes.bool
};
export default AdAccountConnectModalContainer;